<template lang="html">
  <layout-public class="log-in">
    <div class="text-center">
      <hr class="hr-salmon">
      <h1>Log In</h1>

      <div v-show="loginStage == 'login'">
        <input v-model="email" name="email" class="form-control" placeholder="Enter email" type="text" @keyup.enter="logIn"/>

        <input v-model="password" name="password" class="form-control" placeholder="Enter password" type="password" @keyup.enter="logIn"/>

        <list-errors :errors="errors"></list-errors>

        <p><button v-show="!loading" @click.prevent="logIn" class="btn btn-green" id="logInBtn">Log In<i class="fas fa-angle-right fa-fw"></i></button></p>

        <p v-if="loading"><i class="far fa-fw fa-pulse fa-spinner"></i></p>

        <p class="sm"><router-link to="/reset-password">Forgot password?</router-link></p>
      </div>

      <div v-show="loginStage == 'recaptcha'">

        <p><em>Please solve the Recaptcha below to continue logging in:</em></p>
        <div id="recaptchaContainer"></div>
        <list-errors :errors="errors"></list-errors>
      </div>

      <div v-if="loginStage == 'phoneCode'">
        <input v-model="mfaCode" name="mfaCode" class="form-control" placeholder="Enter code from your text message" type="text" @keyup.enter="loginWithPhoneCode"/>

        <list-errors :errors="errors"></list-errors>

        <p><button v-if="!loading" @click.prevent="loginWithPhoneCode" class="btn btn-green">Log In with Code<i class="fas fa-angle-right fa-fw"></i></button></p>

        <p v-if="loading"><i class="far fa-fw fa-pulse fa-spinner"></i></p>
      </div>
      

      <hr class="hr-green">

      <p>Logging in requires access to MFA (multi-factor authentication) via phone. Need help? Ask a team lead.</p>

    </div>
  </layout-public>
</template>

<script>
import {mapGetters} from 'vuex'
import LayoutPublic from '../components/LayoutPublic.vue'
import ListErrors from '../components/ListErrors.vue'
import Firebase from 'firebase/compat/app'
import 'firebase/compat/auth'

export default {
  data () {
    return {
      email: '',
      password: '',
      errors: [],
      loading: false,
      loginStage: 'login',
      mfaResolver: null,
      mfaCode: null,
      mfaVerificationId: null,
      recaptchaVerifier: null,
    }
  },
  components: {
    LayoutPublic, ListErrors
  },
  computed: {
    ...mapGetters(['user']),
    // get next route from querystring, set in FirebaseApp.js, or default to root
    nextRoute () {
      return this.$route.query.redirect || '/'
    },
  },
  watch: {
    //  when signUp completes, go to requested page
    user (auth) {
      if(auth) {
        this.$router.replace(this.nextRoute)
      }
    },
  },
  methods: {
    async logIn () {
      // reset errors
      this.errors = [];
      this.loading = true;
      //  attempt logIn
      let result = await this.$firebase.logIn(this.email, this.password);
      // if result doesn't contain user, it's an error
      // console.log(result);
      if(!result.user) {
        this.loading = false;
        if(result.code == "auth/multi-factor-auth-required" && result.resolver) {
          this.mfaResolver = result.resolver;
          this.loginStage = 'recaptcha';
          this.createRecaptcha();
        }
        else if(result.code == "auth/invalid-email") {
          this.errors.push("Oops, that's not a valid email address.")
        }
        else if(result.code == "auth/wrong-password") {
          this.errors.push("Oops, the password or email address is incorrect.")
        }
        else if(result.code == "auth/user-not-found") {
          this.errors.push("Oops, there is no account using that email address.")
        }
        else {
          this.errors.push(result.message);
        }
      }
    },
    async sendPhoneCode() {
      // console.log('sendPhoneCode called');
      this.loading = true;
      var phoneInfoOptions = {
        multiFactorHint: this.mfaResolver.hints.find(hint => hint.factorId == 'phone'),
        session: this.mfaResolver.session,
      };
      let result = await this.$firebase.sendPhoneVerification(phoneInfoOptions, this.recaptchaVerifier);
      // console.log('sendPhoneVerification result', result);
      if(result && !result.code && !result.message) {
        this.mfaVerificationId = result;
        // do next step to enter phone code
        this.loginStage = 'phoneCode';
      } else {
        this.errors.push('unable to send phone verification code.')
        // console.error('sendPhoneCode error',result);
        // clear recaptcha
        this.recaptchaVerifier.clear();
      }
      this.loading = false;
    },
    async loginWithPhoneCode() {
      // console.log('loginWithPhoneCode');
      this.loading = true;
      let result = await this.$firebase.signInWithPhoneCode(this.mfaResolver, this.mfaVerificationId, this.mfaCode);
      // console.log('signInWithPhoneCode result', result);
      if(result && !result.code && !result.message) {
        // wait for redirect when user found
        console.log('successfully logged in, waiting to redirect');
      } else {
        this.errors.push('unable to log in with code.')
        // console.log('loginWithPhoneCode error', result);
      }
      this.loading = false;
    },
    handleRecaptchaResponse(response) {
      // reCAPTCHA solved, you can proceed with phoneAuthProvider.verifyPhoneNumber(...).
      // console.log('reCAPTCHA solved', response);
      console.log('reCAPTCHA solved');
      // do not move login stage here, required to have recaptcha element visible when calling sendPhoneVerification
      this.sendPhoneCode();
    },
    handleRecaptchaError(response) {
      // console.log('reCAPTCHA error', response);
      console.log('reCAPTCHA error');
      this.recaptchaVerifier.clear();
      this.recaptchaVerifier.reset();
    },
    resetRecaptcha() {
      // console.log('called resetRecaptcha');
      this.recaptchaVerifier.clear();
      this.recaptchaVerifier = null;
      this.createRecaptcha();
    },
    async createRecaptcha() {
      // v1 call from imported firebase library directly
      // wait for next tick so container is visible
      this.$nextTick(() => {
        this.recaptchaVerifier = new Firebase.auth.RecaptchaVerifier('recaptchaContainer', {
          // 'size': 'invisible',
          'size': 'normal',
          'callback': this.handleRecaptchaResponse,
          'expired-callback': this.resetRecaptcha,
          'error-callback': this.handleRecaptchaError,
        });
        this.recaptchaVerifier.verify();
      });
    },
  },
  mounted () {
    // on page refresh, if user, go to requested page
    if(this.user) {
      this.$router.replace(this.nextRoute)
    }
  },
}
</script>

<style lang="less" scoped>
.hr-salmon {
  margin-top: 0;
}
</style>
